import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { sureApiCall } from "src/api/axiosClient/sureApi";
import { fetchAllHydraMembers } from "src/api/_common/hydra/fetchAllHydraMembers";
import { ActionsListSchema } from "src/api/sure-api/actions/useGetOneAction";
import { DataListOptionSchema } from "src/api/sure-api/dataList/useGetAllDataList";
import { TemplateSsqVersionSchema } from "./useTemplateCreationOptions";

const UnitSchema = z.object({
    id: z.number().nullish(),
    code: z.string().nullish(),
    abbreviation: z.string().nullish(),
});

export const TemplateActionFieldSchema = z.object({
    id: z.number(),
    question: z.string(),
    field: z.object({ type: z.string() }).nullish(),
    unit: UnitSchema.nullish(),
    contributionTypes: z.array(z.string()).default([]),
    actionFormulas: z
        .array(
            z.object({
                targetSsqProperty: z
                    .object({ id: z.number().optional(), title: z.string().optional(), unit: UnitSchema.nullish() })
                    .nullish(),
            }),
        )
        .default([]),
    dataList: z.object({ options: z.array(DataListOptionSchema) }).nullish(),
    defaultValue: z.string().nullish(),
    hidden: z.boolean().default(false),
    isMultivalue: z.boolean().default(false),
});

export const TemplateCatalogDetailSchema = z.object({
    id: z.number(),
    title: z.string(),
    actionFields: z.array(TemplateActionFieldSchema),
});

export const TemplateSsqInformationSchema = z.object({
    title: z.string(),
    versions: TemplateSsqVersionSchema.array(),
});

export type TemplateActionField = z.infer<typeof TemplateActionFieldSchema>;

export const useTemplateAvailableActions = (ssqVersionId: number | undefined) => {
    const { i18n } = useTranslation();

    return useQuery({
        queryKey: ["templateAvailableActions", ssqVersionId, i18n.resolvedLanguage],
        enabled: ssqVersionId !== undefined,
        queryFn: async ({ signal }) =>
            ActionsListSchema.array().parse(
                await fetchAllHydraMembers({
                    client: sureApiCall,
                    endpoint: "/api/actions",
                    params: { ssqVersionId, "order[position]": "asc" },
                    requestConfig: { signal },
                }),
            ),
    });
};

export const useTemplateActionFields = (actionId: number | undefined, ssqVersionId: number | undefined) => {
    const { i18n } = useTranslation();

    return useQuery({
        queryKey: ["templateActionFields", actionId, ssqVersionId, i18n.resolvedLanguage],
        enabled: actionId !== undefined && ssqVersionId !== undefined,
        queryFn: async ({ signal }) =>
            TemplateCatalogDetailSchema.parse(
                (await sureApiCall.get(`/api/actions/${actionId}`, { signal, params: { ssqVersionId } })).data,
            ),
    });
};

export const useTemplateSsqInformation = (ssqId: number, enabled = true) => {
    const { i18n } = useTranslation();

    return useQuery({
        queryKey: ["templateSsqInformation", ssqId, i18n.resolvedLanguage],
        enabled,
        queryFn: async ({ signal }) =>
            TemplateSsqInformationSchema.parse((await sureApiCall.get(`/api/ssqs/${ssqId}`, { signal })).data),
    });
};
